import { Backdrop, Box, CircularProgress } from "@mui/material";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as constants from "../../../constants";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import { resetSubChartDetails } from "../../../store/Financials/saveChartSlice";
import { RootState } from "../../../store/store";
import basestyle from "../../CSS/Base.module.css";
import CancelPopUp from "../../PopUp/PopUp";
import { getBudgetDetailsToDownload, getBudgetForecastEligibleYearByParticipantIdAndYear, getChartAccountsByYearAndParticipantIdURL, getParticipantForecastStatusByParticipantIdAndFinancialYear, postForecastChartAccountsTransactions, updateStatusAndActualSubmissionDateByParticipantIdAndFinancialYear } from "../services/Financials/budget.service";
import { ForecastChartIdReq, ForecastChartIdVO } from "../services/Financials/financials";
import { SectionTitle, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer, SubSectionTitle } from "../StyledComponents/Container";
import CommonUtilities from "../Utils/CommonUtilities";
import { formatCurrency, formatCurrencywithoutDoller } from "../Utils/formatUtils";
import { notifyUser } from "../Utils/notifyUser";
import ReusableChartsAccountTable, { Column } from "./ReusableChartsAccountTable";

const BudgetDetails = () => {
    const { state } = useLocation();
    const [chartAccountsVOList, setChartAccountsVOList] = useState<any[]>([]);
    const [chartIncomeAccountsList, setChartIncomeAccountsList] = useState([]);
    const [chartExpensesAccountsList, setChartExpensesAccountsList] = useState([]);
    const [totalIncome, setTotalIncome] = useState(0);
    const [tockenId, setTockenId] = useState(0);
    const [totalExpenses, settotalExpenses] = useState(0);
    const [loadingFlag, setLoadingFlag] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [expandAll, setExpandAll] = useState(false);
    const [participantId, setParticipantId] = useState(0);
    const [currentYear, setCurrentYear] = useState(0);
    const forecastYear = state?.forecastYear ?? "";
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [openPopUp, setOpenPopUp] = useState(false);
    const [readOnlyForecast, setReadOnlyForecast] = useState(false);
    const [markasReadFlag, setMarkasReadFlag] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const userDetails = useSelector((state: RootState) => state.auth);
    const subChartAccountsTableList = useSelector((state: RootState) => state.subChartAccountsTable);
    const subChartSaveVO = useSelector((state: RootState) => state.subChart);
    const [saveUpdatedChartAccountsList, setSaveUpdatedChartAccountsList] = useState<any[]>([]);
    const participantProfile = useSelector((state: RootState) => state.participantAdminProfile);
    const { t } = useTranslation();
    const translate = t as (key: string) => string;

    useEffect(() => {
        if (participantId && currentYear) {
            getBudgetForecastEligibleYearInfo();
        }
    }, [participantId, currentYear]);

    async function getBudgetForecastEligibleYearInfo() {
        try {
            const responseList = await getBudgetForecastEligibleYearByParticipantIdAndYear(participantId, currentYear, userDetails.clientId);
            if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                setReadOnlyForecast(responseList?.data?.data?.readOnly);
            } else {
                setReadOnlyForecast(false);
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        }
    }
    useEffect(() => {
        setParticipantId(userDetails?.defaultParticipantId);
        const currentYear = parseInt(forecastYear.substring(2, 4)) + 2000;
        setCurrentYear(currentYear);
    }, [userDetails]);

    useEffect(() => {
        const updateChartAccount = (index: number) => {
            setChartAccountsVOList(prev => {
                const updatedList = [...prev];
                updatedList[index] = {
                    ...updatedList[index],
                    forecastSubChartAccountsList: subChartAccountsTableList,
                };
                return updatedList;
            });
        };
        const index = 0;
        updateChartAccount(index);
    }, [subChartAccountsTableList]);

    const columns: Column[] = [
        { id: "accountNumber", label: translate('chartAccountNo'), minWidth: 150, align: "center" },
        { id: "accountName", label: translate('description'), minWidth: 150, align: "left" },
        { id: "prevBudget", label: `${translate('budget')} (${currentYear - 2} - ${currentYear - 1})`, minWidth: 150, align: "right" },
        { id: "curBudget", label: `${translate('budget')} (${currentYear - 1} - ${currentYear})`, minWidth: 200, align: "right" },
        { id: "incDec", label: translate('increase/Decr'), minWidth: 200, align: "right" },
        { id: "percentage", label: '%', minWidth: 200, align: "center" },
    ];
    useEffect(() => {
        if (participantId && forecastYear) {
            getChartAccountsInfo();
            getParticipantForecastStatusInfo();
        }
    }, [participantId, forecastYear, expandAll]);



    async function getParticipantForecastStatusInfo() {
        try {
            const responseList = await getParticipantForecastStatusByParticipantIdAndFinancialYear(participantId, forecastYear, userDetails.clientId);
            if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                if (responseList?.data?.data?.status == constants.RESPONSE_SUBMITTED) {
                    setMarkasReadFlag(true);
                } else {
                    setMarkasReadFlag(false);
                }
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        }
    }

    async function getChartAccountsInfo() {
        setIsLoading(true);
        try {
            const responseList = await getChartAccountsByYearAndParticipantIdURL(forecastYear, participantId, userDetails.clientId);
            if (responseList?.status === constants.STATUS_CODES.SUCCESS && responseList.data.data.forecastChartAccountsVOList) {
                setTockenId(responseList.data.data.tockenId)
                const chartAccountsVOList = responseList.data.data.forecastChartAccountsVOList;
                setChartAccountsVOList(responseList.data.data.forecastChartAccountsVOList);
                if (chartAccountsVOList?.length > 0) {
                    const filteredChartIncomeAccounts = chartAccountsVOList.filter((income: { accountType: string; }) => income.accountType === "income");
                    const filteredChartExpensesAccounts = chartAccountsVOList.filter((expenses: { accountType: string; }) => expenses.accountType === "expenses");
                    setChartIncomeAccountsList(filteredChartIncomeAccounts);
                    setChartExpensesAccountsList(filteredChartExpensesAccounts);
                    if (chartAccountsVOList) {
                        let incomeTotal = 0;
                        let expensesTotal = 0;

                        for (const account of chartAccountsVOList) {
                            const amount = Number(account.curBudget) || 0;
                            if (account.accountType === 'income') {
                                incomeTotal += amount;
                            } else if (account.accountType === 'expenses') {
                                expensesTotal += amount;
                            }
                        }
                        setTotalIncome(incomeTotal);
                        settotalExpenses(expensesTotal);
                    }

                } else {
                    setChartIncomeAccountsList([]);
                    setChartExpensesAccountsList([]);
                }
            } else {
                setChartIncomeAccountsList([]);
                setChartExpensesAccountsList([]);
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        } finally {
            setIsLoading(false);
        }
    }


    const transformedChartIncomeAccountsList = chartIncomeAccountsList.map((item: any) => ({
        ...item,
        accountNumber: item.accountNumber < 10000 ? item.accountNumber : '',
        prevBudget: item.prevBudget ? formatCurrency(item.prevBudget) : "$0.00",
        curBudget: item.curBudget ? formatCurrency(item.curBudget) : "$0.00",
        incDec: item.curBudget && item.prevBudget ? formatCurrency(item.curBudget - item.prevBudget) : "$0.00",
        percentage: item.curBudget && item.prevBudget ? `${(((item.curBudget - item.prevBudget) / item.curBudget) * 100).toFixed(2)}%` : "-",
    }));
    const transformedChartExpensesAccountsList = chartExpensesAccountsList.map((item: any) => ({
        ...item,
        accountNumber: item.accountNumber < 10000 ? item.accountNumber : '',
        prevBudget: item.prevBudget ? formatCurrency(item.prevBudget) : "$0.00",
        curBudget: item.curBudget ? formatCurrency(item.curBudget) : "$0.00",
        incDec: item.curBudget && item.prevBudget ? formatCurrency(item.curBudget - item.prevBudget) : "$0.00",
        percentage: item.curBudget && item.prevBudget ? `${(((item.curBudget - item.prevBudget) / item.curBudget) * 100).toFixed(2)}%` : "-",
    }));

    const handleCancel = () => {
        setOpenPopUp(true);
    };
    const handlePopUpClose = () => {
        setOpenPopUp(false);
    };
    function handleConfirm() {
        navigate("/budgetListing");
    }
    const handleBack = () => {
        navigate("/budgetListing");
    };

    const handleReset = () => {
        dispatch(resetSubChartDetails());
        setRefreshKey(prev => prev + 1);
    };

    async function handleSave() {
        const finalRows = subChartSaveVO.subchartArray;

        if (finalRows.length === 0) {
            notifyUser(constants.ERROR_VALIDATION_MESSAGE_NO_VALUE_HAVE_BEEN_MODIFIED, 'error', translate('budget'));
            return;
        }

        setLoadingFlag(true);

        try {
            const tempList = finalRows.map((updatedSubChart) => {
                const parentChart = chartAccountsVOList.find(
                    (chart) => chart.id === updatedSubChart.forecastChartId
                );
                if (!parentChart) return null;

                return {
                    forecastSubChartId: updatedSubChart.id,
                    forecastChartMonth: updatedSubChart.subChartAccountsTransactions.map((txn: any) => ({
                        id: txn.id,
                        month: txn.month,
                        forecastAmount: txn.forecastAmount,
                    })),
                    subChartAccountsNotesVO: {
                        id: updatedSubChart.subChartAccountsNotesVO?.id ?? 0,
                        forecastSubChartId: updatedSubChart.id,
                        version: updatedSubChart.subChartAccountsNotesVO?.version ?? 0,
                        budgetNotes: updatedSubChart.subChartAccountsNotesVO?.budgetNotes ?? '',
                        actualNotes: updatedSubChart.subChartAccountsNotesVO?.actualNotes ?? '',
                    },
                };
            });

            const updatedForecastChartIdVOList: ForecastChartIdVO[] = tempList.filter(
                (item): item is ForecastChartIdVO => item !== null
            );

            const forecastChartIdReq: ForecastChartIdReq = {
                forecastChartIdVOList: updatedForecastChartIdVOList,
                tockenId: tockenId,
                userName: userDetails.emailId,
                forecastYear: forecastYear,
                participantId: participantId,
            };

            const response = await postForecastChartAccountsTransactions(forecastChartIdReq);

            if (response?.status === constants.STATUS_CODES.SUCCESS) {
                notifyUser(response.data.message, 'success', translate('budget'));
                handleReset();
                getChartAccountsInfo();
                if (expandAll) setExpandAll(false);
            } else {
                notifyUser(response.data.message, 'error', translate('budget'));
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        } finally {
            setLoadingFlag(false);
        }
    }

    async function handleMarkAsCompleted() {
        setLoadingFlag(true);
        try {
            const responseList = await updateStatusAndActualSubmissionDateByParticipantIdAndFinancialYear('Submitted', participantId, forecastYear);
            if (responseList.status === constants.STATUS_CODES.SUCCESS) {
                setMarkasReadFlag(true);
                notifyUser(responseList?.data?.message, 'success', translate('budget'));
            }
            else {
                notifyUser(responseList?.data?.message, 'error', translate('budget'));
            }

        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        } finally {
            setLoadingFlag(false);
        }
    };

    const handleCollapseAll = () => {
        setLoadingFlag(true);
        setTimeout(() => {
            setExpandAll(prev => !prev);
            setLoadingFlag(false);
        }, 2000);
    };


    async function toPrint() {
        const printWindow = window.open("", "_blank", "width=900,height=700");
        if (!printWindow) {
            alert("Popup blocked! Please allow popups for this site.");
            return;
        }

        printWindow.document.write(`
            <html><head><title>Loading...</title></head>
            <body><h2>Budget Details data...</h2></body>
            </html>
        `);

        openPrintWindow(chartAccountsVOList, printWindow, forecastYear, participantProfile?.participantAdminProfile, columns);
    };

    const openPrintWindow = (
        tableData: any[],
        printWindow: Window | null,
        forecastYear: string,
        participantName: string,
        columns: Column[]
    ) => {
        if (!printWindow) return;


        const months = CommonUtilities.monthOrderList;
        const hasData = Array.isArray(tableData) && tableData.length > 0;

        const generateSubChartRows = (subCharts: any[]) => {
            let totalPrevBudget = 0;
            let totalCurBudget = 0;
            let totalIncDec = 0;

            const rows = subCharts.map((sub, subIdx) => {
                let jul = 0, aug = 0, sep = 0, oct = 0, nov = 0, dec = 0;
                let jan = 0, feb = 0, mar = 0, apr = 0, may = 0, jun = 0;

                for (let trans of sub.subChartAccountsTransactions ?? []) {
                    switch (trans.month) {
                        case "Jul": jul = trans.forecastAmount; break;
                        case "Aug": aug = trans.forecastAmount; break;
                        case "Sep": sep = trans.forecastAmount; break;
                        case "Oct": oct = trans.forecastAmount; break;
                        case "Nov": nov = trans.forecastAmount; break;
                        case "Dec": dec = trans.forecastAmount; break;
                        case "Jan": jan = trans.forecastAmount; break;
                        case "Feb": feb = trans.forecastAmount; break;
                        case "Mar": mar = trans.forecastAmount; break;
                        case "Apr": apr = trans.forecastAmount; break;
                        case "May": may = trans.forecastAmount; break;
                        case "Jun": jun = trans.forecastAmount; break;
                    }
                }

                const prev = sub.prevBudget ?? 0;
                const cur = sub.curBudget ?? 0;
                const diff = cur - prev;

                totalPrevBudget += prev;
                totalCurBudget += cur;
                totalIncDec += diff;

                const prevBudget = formatCurrencywithoutDoller(prev);
                const curBudget = formatCurrencywithoutDoller(cur);
                const incDec = formatCurrencywithoutDoller(diff);
                const percentage = prev !== 0 ? `${((diff / prev) * 100).toFixed(2)}` : '-';

                return `
                    <tr style="background-color: ${subIdx % 2 === 0 ? '#fafafa' : '#fff'};">
                        <td style="text-align:center;">${sub.accountNumber}</td>
                        <td style="text-align:left;">&nbsp;&nbsp;&nbsp;${sub.accountName}</td>
                        <td style="text-align:right;">${prevBudget}</td>
                        <td style="text-align:right;">${curBudget}</td> 
                        <td style="text-align:right;">${incDec}</td>
                        <td style="text-align:right;">${percentage}</td>
                        <td style="text-align:right;">${formatCurrencywithoutDoller(jul)}</td>
                        <td style="text-align:right;">${formatCurrencywithoutDoller(aug)}</td>
                        <td style="text-align:right;">${formatCurrencywithoutDoller(sep)}</td>
                        <td style="text-align:right;">${formatCurrencywithoutDoller(oct)}</td>
                        <td style="text-align:right;">${formatCurrencywithoutDoller(nov)}</td>
                        <td style="text-align:right;">${formatCurrencywithoutDoller(dec)}</td>
                        <td style="text-align:right;">${formatCurrencywithoutDoller(jan)}</td>
                        <td style="text-align:right;">${formatCurrencywithoutDoller(feb)}</td>
                        <td style="text-align:right;">${formatCurrencywithoutDoller(mar)}</td>
                        <td style="text-align:right;">${formatCurrencywithoutDoller(apr)}</td>
                        <td style="text-align:right;">${formatCurrencywithoutDoller(may)}</td>
                        <td style="text-align:right;">${formatCurrencywithoutDoller(jun)}</td>
                    </tr>
                `;
            }).join('');

            const formattedTotalPrev = formatCurrencywithoutDoller(totalPrevBudget);
            const formattedTotalCur = formatCurrencywithoutDoller(totalCurBudget);
            const formattedTotalIncDec = formatCurrencywithoutDoller(totalIncDec);
            const totalPercentage = totalPrevBudget ? ((totalIncDec / totalPrevBudget) * 100).toFixed(2) : '-';

            const totalRow = `
                <tr style="background-color: #eaeaea; font-weight: bold;">
                    <td style="text-align:center;"></td>
                    <td style="text-align:right;"> Total</td>
                    <td style="text-align:right;">${formattedTotalPrev}</td>
                    <td style="text-align:right;">${formattedTotalCur}</td>
                    <td style="text-align:right;">${formattedTotalIncDec}</td>
                    <td style="text-align:right;">${totalPercentage}</td>
                    ${months.map(() => `<td style="text-align:right;"></td>`).join('')}
                </tr>
            `;

            return rows + totalRow;
        };

        const tableRows = hasData
            ? tableData.map((row, index) => {
                const accountNumber = row.accountNumber < 10000 ? row.accountNumber : '';
                const desc = row.accountName ?? '';
                const subChartRows = row.forecastSubChartAccountsList?.length ? generateSubChartRows(row.forecastSubChartAccountsList) : '';

                return `
                    <tr style="background-color: ${index % 2 === 0 ? '#f1f3f4' : '#fff'};">
                        <td style="text-align:center; font-weight: 600;">${accountNumber}</td>
                        <td style="text-align:left; font-weight: 600;">${desc}</td>
                        <td style="text-align:left; font-weight: 600;"> </td> 
                        <td style="text-align:left; font-weight: 600;"> </td> 
                        <td style="text-align:left; font-weight: 600;"> </td> 
                        <td style="text-align:left; font-weight: 600;"> </td>  
                        ${months.map(() => `<td style="text-align:right;"></td>`).join('')}
                    </tr>
                    ${subChartRows}
                `;
            }).join('')
            : `
                <tr>
                    <td colspan="5" align="center" sx={{ py: 1 }}>
                        No records found
                    </td>
                </tr>
            `;
        const tableHeaders = columns.map((column) => {
            let label = column.label;
            if (label === "Charts Account No") {
                label = "Account No";
            } else if (label === "Increase/Decr." || label.includes("Budget")) {
                label = "$";
            }
            return `
                <th style="text-align: ${column.align}; padding: 8px; border: 1px solid #ddd; background-color: #f5f5f5;">
                    ${label}
                </th>
            `;
        }).join('');

        const visibleMonths = months.filter(month => {
            return tableData.some(row =>
                (row.forecastSubChartAccountsList ?? []).some((subchart: any) =>
                    (subchart.subChartAccountsTransactions ?? []).some((trans: any) =>
                        trans.month === month
                    )
                )
            );
        });

        const monthHeaders = visibleMonths.map(month => `
            <th style="text-align:center; padding: 8px; border: 1px solid #ddd; background-color: #f5f5f5;">
                ${month}
            </th>
        `).join('');

        const fullTableHeaders = tableHeaders + monthHeaders;
        const budgetColumns = columns.filter(col => col.label.includes("Budget"));
        const hasIncrease = columns.some(col => col.label === "Increase/Decr.");
        const hasVariance = columns.some(col => col.label === "%");
        const budgetColumnHeaders = budgetColumns.map(budgetCol => `
    <th style="text-align:center; background-color: #f5f5f5; border: 1px solid #ddd; font-weight: bold;">${budgetCol.label}</th> 
    `).join('\n');
        const additionalHeaders = `
    ${hasIncrease ? `<th style="text-align:center; background-color: #f5f5f5; border: 1px solid #ddd; font-weight: bold;">Increase/Decr.</th>` : ''}
    ${hasVariance ? `<th style="text-align:center; background-color: #f5f5f5; border: 1px solid #ddd; font-weight: bold;">Variance</th>` : ''}
`;


        const combinedGroupHeaderRow = (budgetColumns.length > 0 && visibleMonths.length > 0)
            ? ` <tr> 
            <th style="text-align:left; font-weight: 600;"></th>
            <th style="text-align:left; font-weight: 600;"></th> 
            ${budgetColumnHeaders}
            ${additionalHeaders}
             <th colspan="${visibleMonths.length}" style="text-align:center; background-color: #f5f5f5; border: 1px solid #ddd; font-weight: bold;">Months</th>
        </tr>  `
            : '';


        const tableHtml = `
            <html>
            <head>
                <title >Budget ${forecastYear} - ${participantName}</title>
                <style>
                    @page {
                        size: auto;
                        margin: 10mm 10mm 10mm 10mm;
                    }
                    body {
                        font-family: Inter, sans-serif;
                        margin: 0;
                        padding: 10px;
                        zoom: 0.4;
                    }
                    table {
                        border-collapse: collapse;
                        width: 100%;
                        margin-top: 20px;
                        table-layout: fixed;
                        word-wrap: break-word;
                        font-size: 10px;
                    }
                    th, td {
                        padding: 4px;
                        border: 1px solid #ddd;
                        text-align: right;
                    }
                    th {
                        background-color: #f5f5f5;
                        font-weight: bold;
                    }
                    tr:nth-child(even) {
                        background-color: #f9f9f9;
                    }
                    h1 {
                        text-align: center;
                        font-size: 18px;
                    }
                </style>
            </head>
            <body style="zoom:0.6;">
                <h1>${participantName}</h1>
                <table>
                    <thead>
                        ${combinedGroupHeaderRow}
                        <tr>
                            ${fullTableHeaders}
                        </tr>
                    </thead>
                    <tbody>
                        ${tableRows}
                    </tbody>
                </table>
                <script>
                    window.onload = function () {
                        window.print();
                    };
                </script>
            </body>
            </html>
        `;

        printWindow.document.open();
        printWindow.document.write(tableHtml);
        printWindow.document.close();
    };

    async function toDownload() {
        setLoadingFlag(true);
        try {
            const responseList = await getBudgetDetailsToDownload(forecastYear, participantId, userDetails.clientId);
            if (responseList?.status === constants.STATUS_CODES.SUCCESS && responseList.data.data.documentContent) {
                const base64Data = responseList.data.data.documentContent;

                const byteCharacters = atob(base64Data);
                const byteArrays: Uint8Array[] = [];

                for (let offset = 0; offset < byteCharacters.length; offset += 512) {
                    const slice = byteCharacters.slice(offset, offset + 512);
                    const byteNumbers = new Array(slice.length);

                    for (let i = 0; i < slice.length; i++) {
                        byteNumbers[i] = slice.charCodeAt(i);
                    }

                    const byteArray = new Uint8Array(byteNumbers);
                    byteArrays.push(byteArray);
                }

                const blob = new Blob(byteArrays, { type: "application/octet-stream" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = responseList.data.data.filename ?? "download";
                document.body.appendChild(a);
                a.click();
                URL.revokeObjectURL(url);
                document.body.removeChild(a);

                notifyUser(responseList?.data?.message, 'success', translate('budget'));
            } else {
                notifyUser(responseList?.data?.message, 'error', translate('budget'));
            }

        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        } finally {
            setLoadingFlag(false);
        }
    }
    const handleFinalDataUpdate = (finalUpdatedData: any[]) => {
        setSaveUpdatedChartAccountsList(finalUpdatedData)
    };
    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>{translate('budget')} {forecastYear}</SectionTitle>
                    <Box sx={{ mx: 2 }}>
                        <Box className={basestyle.buttoncontainer} sx={{ mt: -1 }}>
                            <ContainedButton onClick={handleCollapseAll}> {expandAll ? translate('collapseAll') : translate('expandAll')}</ContainedButton>
                            <ContainedButton onClick={toDownload} >{translate('download')}</ContainedButton>
                            <ContainedButton onClick={toPrint} >{translate('print')}</ContainedButton>
                        </Box>
                        <SubSectionTitle>{translate('incomeSummary')}</SubSectionTitle>
                        <ReusableChartsAccountTable
                            tableKey="chartBudgetIncomeAccountsListing"
                            columns={columns}
                            rows={transformedChartIncomeAccountsList}
                            chartAccountsVOList={chartAccountsVOList}
                            loading={isLoading}
                            readOnlyForecast={readOnlyForecast}
                            forecastYear={forecastYear}
                            totalAmount={totalIncome}
                            isExpand={expandAll}
                            pageType="BudgetDetails"
                            onDataChange={handleFinalDataUpdate}
                            refreshKey={refreshKey}
                        />

                        <SubSectionTitle>{translate('expensesSummary')}</SubSectionTitle>

                        <ReusableChartsAccountTable
                            tableKey="chartBudgetExpensesAccountsListing"
                            columns={columns}
                            rows={transformedChartExpensesAccountsList}
                            chartAccountsVOList={chartAccountsVOList}
                            loading={isLoading}
                            readOnlyForecast={readOnlyForecast}
                            forecastYear={forecastYear}
                            totalAmount={totalExpenses}
                            isExpand={expandAll}
                            pageType="BudgetDetails"
                            onDataChange={handleFinalDataUpdate}
                            refreshKey={refreshKey}
                        />

                        <Box className={basestyle.buttoncontainer} >
                            <ContainedButton onClick={handleBack}> {translate('back')}  </ContainedButton>
                            <ContainedButton onClick={handleSave}  > {translate('save')} </ContainedButton>
                            <ContainedButton onClick={handleMarkAsCompleted} disabled={markasReadFlag}>{translate('markAsCompleted')}</ContainedButton>
                            <OutlinedButton onClick={handleCancel}  > {translate('cancel')}  </OutlinedButton>
                        </Box>
                    </Box>
                    <CancelPopUp
                        open={openPopUp}
                        popupTitle={translate('confirmCancel')}
                        handleClose={handlePopUpClose}
                        saveChanges={() => handleConfirm()}
                        message={translate('doYouWantToCancelChanges?')}
                    />
                    <Backdrop
                        sx={{
                            color: "#fff",
                            zIndex: (theme) => theme.zIndex.drawer + 1,
                            display: "flex",
                            flexDirection: "column",
                        }}
                        open={loadingFlag}
                    >
                        <CircularProgress color="inherit" />
                        <SubSectionTitle  >{translate('pleasewait')}</SubSectionTitle>
                    </Backdrop>
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer>
    );
};

export default BudgetDetails;